import { html, LitElement } from 'lit';
import { property, query, queryAll, state } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import {
  campaigns,
  channels,
  chevronDown,
  external,
  gear,
  graph,
  home,
  logout,
  sidebarCollapsed,
  user,
  wallet,
} from '@triptease/icons';
import '@triptease/tt-combobox';
import { styles } from './styles.js';
import { tripteaseLogo } from './triptease-logo.js';
import { reportingUrlMapping, urlMappings } from './urlMappings.js';
import { Routes } from './Routes.js';
import { Config } from './Config.js';

const jsonArrayConverter = (value: string | null) => {
  if (!value) return [];
  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
};

export class TtNavbar extends LitElement {
  static styles = styles;

  @property({ type: Function })
  navigate: ((e: MouseEvent) => void) | undefined;

  @property({ type: String, attribute: 'campaign-manager-url' })
  campaignManagerUrl: string = 'https://app.campaign-manager.triptease.io';

  @property({ type: String, attribute: 'platform-url' })
  platformUrl?: string;

  @property({ type: String, attribute: 'client-key' })
  clientKey?: string;

  @property({ type: String, attribute: 'active-route' })
  activeRoute?: keyof typeof Routes;

  @property({ type: Array, converter: jsonArrayConverter })
  clients: { clientKey: string; displayName: string }[] = [];

  @property({ type: String, attribute: 'initial-state' })
  initialState: 'open' | 'closed' | undefined = undefined;

  @state()
  isOpen: boolean = true;

  @property({ type: Object })
  onClientChange: ((clientKeySelected: string) => void) | undefined;

  @queryAll('details')
  protected allDetailsElements!: Array<HTMLDetailsElement>;

  @queryAll('a')
  protected allNavLinks!: Array<HTMLAnchorElement>;

  @query(`a#${Routes.CampaignManager}`)
  private campaignManagerLink!: HTMLAnchorElement;

  protected firstUpdated() {
    this.setActiveState();
  }

  public connectedCallback() {
    // Listen for browser back/forward navigation
    window.addEventListener('popstate', this.setActiveState);

    // Listen for Tetris programmatic navigation
    window.addEventListener('tetris:navigate', this.setActiveState);

    super.connectedCallback();

    if (this.initialState) {
      this.isOpen = this.initialState === 'open';
    }
  }

  public disconnectedCallback() {
    window.removeEventListener('popstate', this.setActiveState);
    window.removeEventListener('tetris:navigate', this.setActiveState);

    super.disconnectedCallback();
  }

  private get mappedUrlPatterns(): URLPattern[] {
    return Object.keys(urlMappings).map(
      (pattern) => new URLPattern({ pathname: pattern, baseURL: window.location.origin })
    );
  }

  private _isCampaignManagerRoute(): boolean {
    const reportingUrlPattern = new URLPattern({ pathname: reportingUrlMapping, baseURL: window.location.origin });

    return (
      this.activeRoute === Routes.CampaignManager ||
      reportingUrlPattern.test(window.location.pathname, window.location.origin)
    );
  }

  /*
   * Set the active state for the current page.
   *
   * This function iterates over all nav links and compares the current URL path with the href of each link.
   * If the current URL path starts with the href of a link, the link is marked as active. If multiple links
   * share the same prefix, the longest prefix is used as it is more specific.
   *
   * Example:
   * - Current URL: /channels/123
   * - Nav links:
   *   - /channels
   *   - /channels/123
   *   - /channels/456
   *
   * Loop 1: currentPath = /channels/123, linkPath = /channels, bestMatch = /channels
   * Loop 2: currentPath = /channels/123, linkPath = /channels/123, bestMatch = /channels/123
   * Loop 3: currentPath = /channels/123, linkPath = /channels/456, bestMatch = /channels/123
   * Result: /channels/123 is marked as active
   *
   */
  private setActiveState = () => {
    const currentPath = window.location.pathname;

    let bestMatch: HTMLAnchorElement | undefined;
    let bestMatchLength = 0;

    // Check special cases first, as everything will always match on / and return Dashboard
    if (this._isCampaignManagerRoute()) {
      bestMatch = this.campaignManagerLink;
    }

    if (!bestMatch && this.clientKey) {
      const parsedPath = currentPath.replace(this.clientKey, '$CLIENT_KEY');
      let mappedUrl: string | undefined = urlMappings[parsedPath];

      const matchedPattern = this.mappedUrlPatterns.find((pattern) =>
        pattern.test(currentPath, window.location.origin)
      )?.pathname;

      if (matchedPattern) {
        mappedUrl = urlMappings[matchedPattern];
      }

      if (mappedUrl) {
        const clientSpecificUrl = mappedUrl.replace('$CLIENT_KEY', this.clientKey);
        const links = Object.values(this.allNavLinks);
        bestMatch = links.find((link) => link.href.includes(clientSpecificUrl));
      }
    }

    if (!bestMatch) {
      for (const link of this.allNavLinks) {
        const linkPath = new URL(link.href).pathname;

        if (currentPath.startsWith(linkPath)) {
          if (linkPath.length > bestMatchLength) {
            bestMatch = link;
            bestMatchLength = linkPath.length;
          }
        }
      }
    }

    for (const link of this.allNavLinks) {
      link.classList.remove('current-page');
      if (link.hasAttribute('aria-current')) {
        link.attributes.removeNamedItem('aria-current');
      }
    }

    if (bestMatch) {
      bestMatch.classList.add('current-page');
      bestMatch.setAttribute('aria-current', 'page');

      // Auto-expand parent details if the active link is within a collapsible section
      const parentDetails = bestMatch.closest('details');
      if (parentDetails) {
        parentDetails.setAttribute('open', '');
      }
    } else {
      console.error(`No matching nav link found for path: ${currentPath}`);
    }
  };

  private closeAllDetails = (element?: HTMLDetailsElement) => {
    this.allDetailsElements.forEach((detail) => {
      if (element && !detail.contains(element)) {
        detail.removeAttribute('open');
      } else if (!element) {
        detail.removeAttribute('open');
      }
    });
  };

  private toggleSidebar = () => {
    this.closeAllDetails();
    this.isOpen = !this.isOpen;

    const { NavbarStateCookieName, NavbarStateCookieOpenValue, NavbarStateCookieClosedValue } = Config;

    const domain = window.location.hostname.endsWith('.triptease.io') ? 'domain=.triptease.io' : '';

    document.cookie = `${NavbarStateCookieName}=${this.isOpen ? NavbarStateCookieOpenValue : NavbarStateCookieClosedValue}; path=/; max-age=31536000; ${domain}`;
  };

  private handleDetailsToggle = (e: ToggleEvent) => {
    const { newState } = e;
    const target = e.currentTarget as HTMLDetailsElement;

    if (newState === 'open') {
      if (!this.isOpen) {
        this.toggleSidebar();
      }

      this.closeAllDetails(target);
    }
  };

  private buildUrl = (path: string): string => {
    if (!this.clientKey) throw new Error('clientKey is required');

    const formattedPath = path.replace('$CLIENT_KEY', this.clientKey);
    if (!this.platformUrl) return formattedPath;

    return new URL(formattedPath, this.platformUrl).toString();
  };

  private onAnchorClick = (e: MouseEvent) => {
    if (this.navigate) {
      this.navigate(e);
      this.setActiveState();
    }
  };

  private handleClientChange = (e: Event) => {
    const combobox = e.target as EventTarget & { value?: string[] };
    const selectedClientKey = combobox.value?.[0];

    if (selectedClientKey && this.onClientChange) {
      this.onClientChange(selectedClientKey);
    }
  };

  render() {
    return html`
      <nav id=${this.id} class="${this.isOpen ? '' : 'sidebar-closed'}">
        <div class="sidebar-header ${this.isOpen ? '' : 'sidebar-closed'}">
          <div class="logo">
            ${unsafeSVG(tripteaseLogo)}
          </div>
          <button id="navbar-toggle-btn" class="nav-item nav-toggle-button" @click=${this.toggleSidebar}>
            ${unsafeSVG(sidebarCollapsed)}
            <span class="tooltip nav-toggle-tooltip">
              ${unsafeSVG(sidebarCollapsed)}
              <span>${this.isOpen ? html`Collapse sidebar` : html`Expand sidebar`}</span>
            </span>
          </button>
        </div>

        <div class="nav-items ${this.isOpen ? '' : 'sidebar-closed'}">
          <a
            class="nav-item"
            href=${this.buildUrl('/')}
            @click=${this.onAnchorClick}
            data-intercom-target="dashboard"
          >${unsafeSVG(home)}<span>Dashboard</span></a
          >
          <a id="${Routes.CampaignManager}" class="nav-item" href=${this.campaignManagerUrl}
             data-intercom-target="campaigns"
          >${unsafeSVG(campaigns)}<span>Campaigns</span></a
          >
          <a
            class="nav-item"
            href=${this.buildUrl('/$CLIENT_KEY/channels')}
            @click=${this.onAnchorClick}
            data-intercom-target="channels"
          >${unsafeSVG(channels)}<span>Channels</span></a
          >
          <details id="market-insights" @toggle=${this.handleDetailsToggle} data-intercom-target="market-insights">
            <summary>
              ${unsafeSVG(graph)}
              <span>Market Insights</span>
              <span class="icon chevron"> ${unsafeSVG(chevronDown)}</span>
            </summary>
            <div class="dropdown-items">
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/parity/$CLIENT_KEY')}
                @click=${this.onAnchorClick}
                data-intercom-target="parity-monitoring"
              >Parity monitoring</a
              >
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/$CLIENT_KEY/guest-insights')}
                @click=${this.onAnchorClick}
                data-intercom-target="guest-insights"
              >Guest insights</a
              >
            </div>
          </details>
          <details id="settings" @toggle=${this.handleDetailsToggle} data-intercom-target="settings">
            <summary>
              ${unsafeSVG(gear)}
              <span>Settings</span>
              <span class="icon chevron"> ${unsafeSVG(chevronDown)}</span>
            </summary>
            <div class="dropdown-items">
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/$CLIENT_KEY/guest-behavioural-data')}
                @click=${this.onAnchorClick}
                data-intercom-target="email-setup"
              >Email setup</a
              >
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/$CLIENT_KEY/crm-config')}
                @click=${this.onAnchorClick}
                data-intercom-target="crm-connectivity"
              >CRM connectivity</a
              >
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/$CLIENT_KEY/settings/guides')}
                @click=${this.onAnchorClick}
                data-intercom-target="group-settings"
              >Group settings</a
              >
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/$CLIENT_KEY/settings/hotels')}
                @click=${this.onAnchorClick}
                data-intercom-target="property-settings"
              >Property settings</a
              >
            </div>
          </details>
          <hr />
          <details id="account" @toggle=${this.handleDetailsToggle} data-intercom-target="account">
            <summary>
              ${unsafeSVG(user)}
              <span>Account</span>
              <span class="icon chevron"> ${unsafeSVG(chevronDown)}</span>
            </summary>
            <div class="dropdown-items">
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/account')}
                @click=${this.onAnchorClick}
                data-intercom-target="user-settings"
              >User settings</a
              >
              <!-- TODO: Change to /$CLIENT_KEY/account/team when the change in Tetris is ready -->
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/account/team/$CLIENT_KEY')}
                @click=${this.onAnchorClick}
                data-intercom-target="team-permissions"
              >Team and permissions</a
              >
            </div>
          </details>
          <details id="billing-routes" @toggle=${this.handleDetailsToggle} data-intercom-target="billing">
            <summary>
              ${unsafeSVG(wallet)}
              <span>Billing</span>
              <span class="icon chevron"> ${unsafeSVG(chevronDown)}</span>
            </summary>
            <div>
              <!-- TODO: Change to /$CLIENT_KEY/account/billing-management when the change in Tetris is ready -->
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/account/billing-management/$CLIENT_KEY')}
                @click=${this.onAnchorClick}
                data-intercom-target="booking-reconciliation"
              >Booking reconciliation</a
              >
              <!-- TODO: Change to /$CLIENT_KEY/subscriptions when the change in Tetris is ready -->
              <a
                class="sub-nav-item"
                href=${this.buildUrl('/subscriptions/$CLIENT_KEY')}
                @click=${this.onAnchorClick}
                data-intercom-target="subscriptions"
              >Subscriptions</a
              >
            </div>
          </details>
        </div>
        <div class="tertiary-nav ${this.isOpen ? '' : 'sidebar-closed'}">
          <div id="external-links" class="nav-items">
            <a
              class="nav-item external-link"
              href='https://triptease.canny.io/feature-requests'
              target="_blank"
              rel="noreferrer"
            >
              Feature requests
              ${unsafeSVG(external)}
            </a>
            <a
              class='nav-item external-link'
              href='https://triptease.canny.io/changelog'
              target="_blank"
              rel="noreferrer"
            >
              Product updates
              ${unsafeSVG(external)}
            </a>
            <a
              class='nav-item external-link'
              href='https://help.triptease.com/'
              target="_blank"
              rel="noreferrer"
            >
              Help center
              ${unsafeSVG(external)}
            </a>
            <hr />
          </div>
          <div class='nav-items'>
            <div id="client-selector">
              ${
                this.clients.length > 1
                  ? html`
                      <tt-combobox
                        .openUpward=${true}
                        .value=${this.clientKey ? [this.clientKey] : []}
                        @change=${this.handleClientChange}
                      >
                        ${this.clients.map(
                          (client) => html`
                            <option slot="option" value=${client.clientKey}>${client.displayName}</option>
                          `
                        )}
                      </tt-combobox>
                    `
                  : html`
                      <div class="single-client-name">
                        ${this.clients.find((m) => m.clientKey === this.clientKey)?.displayName}
                      </div>
                    `
              }
            </div>
            <a
              id="logout-link"
              class="nav-item"
              href=${this.buildUrl('/logout')}
              @click=${this.onAnchorClick}
              data-intercom-target="logout"
            >${unsafeSVG(logout)}<span>Logout</span></a>
          </div>
      </nav>
    `;
  }
}
